/**
 * Entity Type Icon Mapping using Lucide Icons
 *
 * The Cytoscape graph draws these same components as its node glyphs
 * (node-icons.ts), so a type's icon set here is also its icon on the graph.
 */

import {
  AlertTriangle,
  ArrowRightLeft,
  AtSign,
  AudioLines,
  Beaker,
  Bitcoin,
  Briefcase,
  Building,
  Building2,
  Camera,
  Car,
  Circle,
  ClipboardCheck,
  Clock,
  Coins,
  CreditCard,
  DollarSign,
  FileAudio,
  FileEdit,
  FileText,
  FolderOpen,
  Fuel,
  Gem,
  Globe,
  Home,
  Image,
  KeyRound,
  Landmark,
  Link2,
  List,
  Lock,
  type LucideIcon,
  Map,
  MapPin,
  Megaphone,
  Phone,
  PhoneCall,
  Radio,
  Receipt,
  ReceiptText,
  Scale,
  ScrollText,
  Search,
  Smartphone,
  StickyNote,
  Table2,
  Ticket,
  TrainFront,
  Unlock,
  User,
  Wallet,
} from 'lucide-react'

/**
 * Icon component mapping for each entity type
 */
export const ENTITY_ICON_COMPONENTS: Record<string, LucideIcon> = {
  // People & Organizations
  Person: User,
  Company: Building2,
  Organization: Landmark,
  Inspector: Search,
  CorporateRole: Briefcase,

  // Legal & Compliance
  Law: Scale,
  Fine: DollarSign,
  FinePayment: CreditCard,
  Agreement: ScrollText,
  Contract: FileText,
  Case: FolderOpen,
  Crime: AlertTriangle,
  Evidence: Beaker,
  Inspection: ClipboardCheck,

  // Documents & Data
  Document: FileText,
  AttachedDocument: FileText,
  Recording: FileAudio,
  Voice: AudioLines,
  SourceSnippet: Table2,
  // About a minute of a recording, written next to it. The canvas draws it as
  // a timecode chip with no glyph (styles.ts); this is its mark everywhere else.
  TranscriptPassage: AudioLines,
  Chunk: Circle,
  IdentificationDocument: CreditCard,
  Badge: Ticket,

  // Financial
  BankAccount: Building,
  Transaction: ArrowRightLeft,
  Payment: Wallet,
  Invoice: ReceiptText,
  LineItem: List,

  // Blockchain/Crypto
  Cluster: Link2,
  ExchangeAccount: Coins,
  BlockchainAddress: Lock,
  CryptoAccount: Bitcoin,
  CryptoAddress: Bitcoin,
  Address: Home,

  // Communication
  Phone: Phone,
  PhoneNumber: Phone,
  PhoneCall: PhoneCall,
  Email: AtSign,
  EmailAddress: AtSign,

  // Location & Geo
  Location: MapPin,
  GeoObservation: Globe,
  Area: Map,

  // Infrastructure (BTS, Fuel, Rail)
  BTSStation: Radio,
  FuelStation: Fuel,
  TrainCrossing: TrainFront,

  // Devices & Assets
  Camera: Camera,
  AccessDevice: Unlock,
  MobileDevice: Smartphone,
  Vehicle: Car,
  Asset: Gem,

  // Social
  SocialAccount: AtSign,

  // Application-to-person senders — shortcodes, brand sender ids, RCS agents
  ServiceSender: Megaphone,

  // Events
  Event: Clock,

  // Media
  Picture: Image,
  Photo: Image,

  // Notes
  StickyNote: StickyNote,
  Note: FileEdit,

  // Access & Permissions
  AccessRight: KeyRound,

  // Financial Obligations
  Penalty: Receipt,

  // Other
  Property: Home,

  // Default fallback
  Default: Circle,
}

/**
 * Get icon component for a given entity type label
 */
export function getEntityIconComponent(typeLabel: string | undefined): LucideIcon {
  if (!typeLabel) return ENTITY_ICON_COMPONENTS.Default
  return ENTITY_ICON_COMPONENTS[typeLabel] || ENTITY_ICON_COMPONENTS.Default
}

/**
 * Entity types that should be rendered as small dots (minimal nodes)
 */
export const MINIMAL_NODE_TYPES = new Set(['Chunk', 'Document', 'CaseDocument', 'LogEvent'])

/**
 * Check if an entity type should be rendered as a minimal node (dot)
 */
export function isMinimalNode(typeLabel: string | undefined): boolean {
  if (!typeLabel) return false
  return MINIMAL_NODE_TYPES.has(typeLabel)
}

/**
 * The hue of a transcript passage: the quietest of the categorical palette,
 * `--cat-10` in index.css. Passages stand around their recording by the dozen,
 * so the hue has to set them apart from the recording (slate) and from the
 * people they mention (blue) without competing with either.
 *
 * Cytoscape, and the inline styles that read the two maps below, take literal
 * colours and cannot read the token: these are its sRGB twins. Retune them
 * together with the token.
 */
const PASSAGE_HUE = '#31a5a5' // frontend-rules-ok: twin of --cat-10, oklch(0.66 0.1 195)
const PASSAGE_HUE_DEEP = '#198585' // frontend-rules-ok: one step down, oklch(0.56 0.09 195)

/**
 * Per-entity-type color palette for graph nodes
 */
export const ENTITY_TYPE_COLORS: Record<string, { bg: string; border: string }> = {
  Person: { bg: '#3b82f6', border: '#2563eb' },
  Company: { bg: '#8b5cf6', border: '#7c3aed' },
  Organization: { bg: '#8b5cf6', border: '#7c3aed' },
  Location: { bg: '#22c55e', border: '#16a34a' },
  BTSStation: { bg: '#f97316', border: '#ea580c' },
  MobileDevice: { bg: '#06b6d4', border: '#0891b2' },
  PhoneNumber: { bg: '#7dd3fc', border: '#38bdf8' },
  Phone: { bg: '#7dd3fc', border: '#38bdf8' },
  EmailAddress: { bg: '#60a5fa', border: '#2563eb' },
  Email: { bg: '#60a5fa', border: '#2563eb' },
  Vehicle: { bg: '#eab308', border: '#ca8a04' },
  FuelStation: { bg: '#ef4444', border: '#dc2626' },
  TrainCrossing: { bg: '#a855f7', border: '#9333ea' },
  SocialAccount: { bg: '#ec4899', border: '#db2777' },
  // Deliberately muted: a service sender is low-signal traffic an analyst
  // scans past, not an identity to follow.
  ServiceSender: { bg: '#94a3b8', border: '#64748b' },
  BankAccount: { bg: '#14b8a6', border: '#0d9488' },
  IBAN: { bg: '#14b8a6', border: '#0d9488' },
  CryptoAccount: { bg: '#f7931a', border: '#e0820d' },
  CryptoAddress: { bg: '#f7931a', border: '#e0820d' },
  Event: { bg: '#f59e0b', border: '#d97706' },
  TranscriptPassage: { bg: PASSAGE_HUE, border: PASSAGE_HUE_DEEP },
  Default: { bg: '#64748b', border: '#475569' },
}

/**
 * Get entity type colors (bg + border) for a given type label
 */
export function getEntityTypeColors(typeLabel: string | undefined): {
  bg: string
  border: string
} {
  if (!typeLabel) return ENTITY_TYPE_COLORS.Default
  return ENTITY_TYPE_COLORS[typeLabel] || ENTITY_TYPE_COLORS.Default
}

/**
 * LABEL_COLORS - per-entity-type colors used by filter panels, legends, and sidebars.
 * Uses { fill, stroke } format for compatibility with UI components.
 */
const DEFAULT_COLOR = { fill: '#64748b', stroke: '#475569' }
const MINIMAL_COLOR = { fill: '#94a3b8', stroke: '#dfe3e9' }

export const LABEL_COLORS: Record<string, { fill: string; stroke: string }> = {
  // People & Organizations
  Person: { fill: '#3b82f6', stroke: '#2563eb' },
  TypesensePerson: { fill: '#3b82f6', stroke: '#2563eb' },
  Company: { fill: '#8b5cf6', stroke: '#7c3aed' },
  TypesenseCompany: { fill: '#8b5cf6', stroke: '#7c3aed' },
  Organization: { fill: '#8b5cf6', stroke: '#7c3aed' },
  CorporateRole: { fill: '#8b5cf6', stroke: '#7c3aed' },
  Role: { fill: '#8b5cf6', stroke: '#7c3aed' },

  // Location & Geo
  Location: { fill: '#22c55e', stroke: '#16a34a' },
  Property: { fill: '#22c55e', stroke: '#16a34a' },
  Address: { fill: '#22c55e', stroke: '#16a34a' },

  // Infrastructure (BTS, Fuel, Rail)
  BTSStation: { fill: '#f97316', stroke: '#ea580c' },
  FuelStation: { fill: '#ef4444', stroke: '#dc2626' },
  TrainCrossing: { fill: '#a855f7', stroke: '#9333ea' },

  // Devices & Communication
  MobileDevice: { fill: '#06b6d4', stroke: '#0891b2' },
  PhoneNumber: { fill: '#7dd3fc', stroke: '#38bdf8' },
  Phone: { fill: '#7dd3fc', stroke: '#38bdf8' },
  EmailAddress: { fill: '#60a5fa', stroke: '#2563eb' },
  Email: { fill: '#60a5fa', stroke: '#2563eb' },
  Vehicle: { fill: '#eab308', stroke: '#ca8a04' },

  // Social
  SocialAccount: { fill: '#ec4899', stroke: '#db2777' },
  ServiceSender: { fill: '#94a3b8', stroke: '#64748b' },

  // Financial
  BankAccount: { fill: '#14b8a6', stroke: '#0d9488' },
  IBAN: { fill: '#14b8a6', stroke: '#0d9488' },
  ExchangeAccount: { fill: '#14b8a6', stroke: '#0d9488' },
  Payment: { fill: '#14b8a6', stroke: '#0d9488' },
  Transaction: { fill: '#14b8a6', stroke: '#0d9488' },

  // Crypto wallets — Bitcoin orange so they read as distinct from fiat accounts
  CryptoAccount: { fill: '#f7931a', stroke: '#e0820d' },
  CryptoAddress: { fill: '#f7931a', stroke: '#e0820d' },

  // Events
  Event: { fill: '#f59e0b', stroke: '#d97706' },
  OffchainEvent: { fill: '#f59e0b', stroke: '#d97706' },
  DailySummary: { fill: '#f59e0b', stroke: '#d97706' },
  GroupOfDailySummaries: { fill: '#f59e0b', stroke: '#d97706' },
  DailyTrading: { fill: '#f59e0b', stroke: '#d97706' },

  // A passage of a recording (same pair as ENTITY_TYPE_COLORS)
  TranscriptPassage: { fill: PASSAGE_HUE, stroke: PASSAGE_HUE_DEEP },

  // Documents
  Document: DEFAULT_COLOR,
  Recording: DEFAULT_COLOR,
  Voice: DEFAULT_COLOR,
  Agreement: DEFAULT_COLOR,
  Contract: DEFAULT_COLOR,
  Case: DEFAULT_COLOR,
  Report: DEFAULT_COLOR,

  // Crypto
  Cluster: DEFAULT_COLOR,

  // Minimal
  Chunk: MINIMAL_COLOR,

  // Notes & Media
  Picture: DEFAULT_COLOR,
  Note: DEFAULT_COLOR,
  StickyNote: DEFAULT_COLOR,

  Default: DEFAULT_COLOR,
}

/**
 * Unified node color scheme - theme-aware
 */
export type GraphTheme = 'dark' | 'light'

const DARK_NODE_COLORS = {
  bg: '#64748b', // slate-500
  border: '#475569', // slate-600
  text: '#ffffff',
  selectedBg: '#3b82f6',
  selectedBorder: '#2563eb',
  minimalBg: '#94a3b8',
  minimalBorder: '#64748b',
  hoverBg: '#94a3b8',
  hoverBorder: '#64748b',
  suspectBg: '#ef4444',
  suspectBorder: '#dc2626',
  crimeRelatedBg: '#ef4444',
  crimeRelatedBorder: '#dc2626',
  iconColor: '#ffffff',
  // Theme-specific colors used in stylesheet
  canvasBg: '#0a0a0a',
  eventTextColor: '#e2e8f0',
  eventTextBg: '#1e293b',
  docBg: '#1e40af',
  docBorder: '#60a5fa',
  docIconColor: '#ffffff',
  docOverlay: '#3b82f6',
  docTextColor: '#93c5fd',
  docTextBg: '#0f172a',
  sourceDocBg: '#0f766e',
  sourceDocBorder: '#2dd4bf',
  sourceDocOverlay: '#14b8a6',
  sourceDocTextColor: '#5eead4',
  pictureBorder: '#94a3b8',
  pictureBg: '#1e293b',
  suspectTextColor: '#ffffff',
  suspectTextBg: '#000000',
  edgeColor: '#64748b',
  edgeLabelColor: '#94a3b8',
  edgeLabelBg: '#0a0a0a',
  parentBg: '#475569',
  parentBorder: '#64748b',
  parentText: '#e2e8f0',
  caseCompoundBg: '#3b82f6',
  caseCompoundBorder: '#60a5fa',
  caseCompoundText: '#93c5fd',
  caseCompoundTextBg: '#1e293b',
  lodTypeLabelColor: '#94a3b8',
  lodTypeLabelBg: '#0f172a',
} as const

const LIGHT_NODE_COLORS = {
  bg: '#94a3b8', // slate-400
  border: '#cbd5e1', // slate-300
  text: '#1e293b',
  selectedBg: '#3b82f6',
  selectedBorder: '#2563eb',
  minimalBg: '#cbd5e1',
  minimalBorder: '#94a3b8',
  hoverBg: '#64748b',
  hoverBorder: '#475569',
  suspectBg: '#ef4444',
  suspectBorder: '#dc2626',
  crimeRelatedBg: '#ef4444',
  crimeRelatedBorder: '#dc2626',
  iconColor: '#1e293b',
  canvasBg: '#ffffff',
  eventTextColor: '#334155',
  eventTextBg: '#f1f5f9',
  docBg: '#dbeafe',
  docBorder: '#3b82f6',
  docIconColor: '#1e40af',
  docOverlay: '#3b82f6',
  docTextColor: '#1e40af',
  docTextBg: '#ffffff',
  sourceDocBg: '#ccfbf1',
  sourceDocBorder: '#14b8a6',
  sourceDocOverlay: '#14b8a6',
  sourceDocTextColor: '#0f766e',
  pictureBorder: '#cbd5e1',
  pictureBg: '#f8fafc',
  suspectTextColor: '#ffffff',
  suspectTextBg: '#1e293b',
  edgeColor: '#94a3b8',
  edgeLabelColor: '#64748b',
  edgeLabelBg: '#ffffff',
  parentBg: '#cbd5e1',
  parentBorder: '#94a3b8',
  parentText: '#334155',
  caseCompoundBg: '#3b82f6',
  caseCompoundBorder: '#93c5fd',
  caseCompoundText: '#1e40af',
  caseCompoundTextBg: '#ffffff',
  lodTypeLabelColor: '#475569',
  lodTypeLabelBg: '#ffffff',
} as const

export function getNodeColors(theme: GraphTheme) {
  return theme === 'light' ? LIGHT_NODE_COLORS : DARK_NODE_COLORS
}

/** @deprecated Use getNodeColors(theme) instead for theme-aware colors */
export const UNIFIED_NODE_COLORS = DARK_NODE_COLORS
